import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import stories from "../data/stories";

function StoryCard({ story, onOpen, index }) {
  return (
    <motion.button
      type="button"
      onClick={() => onOpen(story.slug)}
      data-testid={`story-card-${story.slug}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay: index * 0.1 }}
      className="group relative text-left overflow-hidden bg-[#F5EFEB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8B4513]"
    >
      <div className="relative aspect-[4/5] overflow-hidden">
        <img
          src={story.heroImage}
          alt={story.title}
          className="w-full h-full object-cover transition-transform duration-[1.4s] group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2A1D15]/70 via-[#2A1D15]/10 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-7 md:p-10 text-[#FDFBF7]">
          <p className="overline text-[#FDFBF7]/80 mb-3">A Story</p>
          <h3 className="font-serif-display text-3xl md:text-4xl leading-tight">
            {story.title}
          </h3>
          <p className="mt-3 text-sm md:text-base font-light text-[#FDFBF7]/80 max-w-md">
            {story.subtitle}
          </p>
          <span className="mt-6 inline-flex items-center gap-2 overline text-[#FDFBF7] border-b border-[#FDFBF7]/40 pb-1">
            Read the story
            <ArrowRight
              size={14}
              className="transition-transform duration-300 group-hover:translate-x-1"
            />
          </span>
        </div>
      </div>
    </motion.button>
  );
}

function IntroSection({ section }) {
  return (
    <div className="max-w-3xl mx-auto text-center py-12 md:py-16">
      <p className="overline text-[#8B4513] mb-4">{section.heading}</p>
      <p className="dropcap text-lg md:text-xl text-[#2A1D15] font-light leading-relaxed text-left mt-6">
        {section.body}
      </p>
    </div>
  );
}

function SplitSection({ section }) {
  const reverse = section.kind === "split-reverse";
  return (
    <div
      className={`grid md:grid-cols-12 gap-10 md:gap-16 items-center py-12 md:py-16 ${
        reverse ? "md:[&>*:first-child]:order-2" : ""
      }`}
    >
      <div className="md:col-span-7">
        <div className="overflow-hidden bg-[#F5EFEB]">
          <img
            src={section.image}
            alt={section.heading}
            className="w-full h-auto object-cover"
          />
        </div>
      </div>
      <div className="md:col-span-5">
        <p className="overline text-[#8B4513] mb-4">{section.heading}</p>
        <p className="text-base md:text-lg text-[#2A1D15] font-light leading-relaxed mt-6">
          {section.body}
        </p>
      </div>
    </div>
  );
}

function WriteupSection({ section }) {
  return (
    <div className="max-w-3xl mx-auto py-12 md:py-20 px-6 md:px-12 bg-[#F5EFEB] my-8">
      <p className="overline text-[#8B4513] mb-4 text-center">
        {section.heading}
      </p>
      <div className="space-y-5 mt-8">
        {section.body.split("\n\n").map((p) => (
          <p
            key={p}
            className={`text-base md:text-lg text-[#2A1D15] font-light leading-relaxed ${
              p.startsWith("—") ? "font-serif-display italic text-[#8B4513]" : ""
            }`}
          >
            {p}
          </p>
        ))}
      </div>
    </div>
  );
}

function MoodboardSection({ section }) {
  return (
    <div className="py-12 md:py-16">
      <p className="overline text-[#8B4513] mb-4 text-center">
        {section.heading}
      </p>
      <div className="grid md:grid-cols-2 gap-6 mt-10">
        {section.images.map((img, i) => (
          <div key={img} className="overflow-hidden bg-[#F5EFEB]">
            <img
              src={img}
              alt={`Moodboard ${i + 1}`}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function GallerySection({ section }) {
  return (
    <div className="py-12 md:py-16">
      <p className="overline text-[#8B4513] mb-6">{section.heading}</p>
      {section.intro && (
        <p className="max-w-2xl text-base md:text-lg text-[#5C4334] font-light leading-relaxed mb-10">
          {section.intro}
        </p>
      )}
      <div className="grid sm:grid-cols-2 gap-8">
        {section.items.map((it) => (
          <figure key={it.image} className="space-y-4">
            <div className="overflow-hidden bg-[#F5EFEB] aspect-[4/3]">
              <img
                src={it.image}
                alt={it.caption}
                className="w-full h-full object-cover"
              />
            </div>
            <figcaption className="text-sm text-[#5C4334] font-light leading-relaxed">
              {it.caption}
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
}

function FinalsSection({ section }) {
  return (
    <div className="py-12 md:py-16">
      <p className="overline text-[#8B4513] mb-4 text-center">
        {section.heading}
      </p>
      <div
        className={`grid gap-6 mt-10 ${
          section.images.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3"
        }`}
      >
        {section.images.map((img) => (
          <div key={img} className="overflow-hidden bg-[#F5EFEB]">
            <img
              src={img}
              alt="Final"
              className="w-full h-auto object-cover"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

const sectionComponents = {
  intro: IntroSection,
  split: SplitSection,
  "split-reverse": SplitSection,
  writeup: WriteupSection,
  moodboard: MoodboardSection,
  gallery: GallerySection,
  finals: FinalsSection,
};

function StorySection({ section }) {
  const Component = sectionComponents[section.kind];
  return Component ? <Component section={section} /> : null;
}

function ModalTopBar({ story, onClose }) {
  return (
    <div className="sticky top-0 z-10 bg-[#FDFBF7]/85 backdrop-blur-xl border-b border-[#8B4513]/10">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        <p className="overline text-[#8B4513]">A Gifting Concierge Story</p>
        <button
          onClick={onClose}
          data-testid={`story-modal-close-${story.slug}`}
          aria-label="Close story"
          className="rounded-full p-2 border border-[#8B4513]/30 text-[#2A1D15] hover:bg-[#8B4513] hover:text-[#FDFBF7] transition-colors"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}

function ModalHero({ story }) {
  return (
    <div className="relative h-[60vh] md:h-[70vh] overflow-hidden">
      <img
        src={story.heroImage}
        alt={story.title}
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#2A1D15]/70 via-[#2A1D15]/20 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16 text-[#FDFBF7] max-w-4xl">
        <h2 className="font-serif-display text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
          {story.title}
        </h2>
        <p className="mt-4 text-lg md:text-xl font-light text-[#FDFBF7]/85 max-w-2xl">
          {story.subtitle}
        </p>
      </div>
    </div>
  );
}

function StoryModal({ story, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="fixed inset-0 z-[100] bg-[#FDFBF7] overflow-y-auto"
      data-testid={`story-modal-${story.slug}`}
    >
      <ModalTopBar story={story} onClose={onClose} />
      <ModalHero story={story} />

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-10 md:py-16">
        {story.sections.map((s) => (
          <StorySection key={`${s.kind}-${s.heading}`} section={s} />
        ))}

        <div className="mt-16 text-center">
          <button
            onClick={onClose}
            data-testid={`story-back-${story.slug}`}
            className="border border-[#8B4513] text-[#8B4513] px-8 py-4 overline hover:bg-[#8B4513] hover:text-[#FDFBF7] transition-colors duration-300"
          >
            Back to all stories
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default function Stories() {
  const [active, setActive] = useState(null);
  const story = stories.find((s) => s.slug === active);

  React.useEffect(() => {
    document.body.style.overflow = active ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [active]);

  return (
    <section
      id="stories"
      data-testid="stories-section"
      className="py-24 md:py-32 bg-[#ebe0cd]"
    >
      <div className="max-w-[1340px] mx-auto px-6 md:px-10 lg:px-16 xl:px-24">
        <div className="max-w-3xl">
          <p className="overline text-[#8B4513] mb-6" data-testid="stories-overline">
            Stories · Inside our process
          </p>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[0.98] tracking-tight text-[#2A1D15]">
            Two gifts.{" "}
            <span className="italic text-[#8B4513]">Two unforgettable journeys.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#5C4334] font-light leading-relaxed">
            Step inside two of our most loved creations — from the first brief
            to the final ribbon.
          </p>
        </div>

        <div className="mt-16 md:mt-20 grid md:grid-cols-2 gap-8">
          {stories.map((s, i) => (
            <StoryCard
              key={s.slug}
              story={s}
              onOpen={setActive}
              index={i}
            />
          ))}
        </div>
      </div>

      <AnimatePresence>
        {story && <StoryModal story={story} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
}
